// ABOUTME: Read-only rendered dashboard at /dashboard/:id. Loads the saved JSON layout and renders each cell's
// ABOUTME: report (chart/pivot dashboard-mode), or renders a layout handed straight in as a `config` prop.
import { Box, ButtonBase, Typography, useTheme } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import cx from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import { ENV_TYPES } from "../Sidebar";
import Text from "../../theme/text/Text";
import Subheading from "../../theme/text/Subheading";
import Shbutton from "../../theme/buttons/Shbutton";
import MConfirmDelete from "../Modals/MConfirmDelete";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";
import { useGetBiDashboardByIdQuery, useGetBiInlineDashboardQuery, useDeleteBiDashboardMutation } from "../../../newapi/global/businessIntelligence";
import { RhDashboardChart } from "./components/RhDashboardChart";
import { RhDashboardPivotFull } from "./components/RhDashboardPivotFull";
import { RhDashboardPivotBounded } from "./components/RhDashboardPivotBounded";
import { RhDashboardComponent } from "./builder/DashboardElement";
// Date control now lives on the full pivot (the only live cell); restore here for a per-dashboard date later.
// import { RhDateSelector } from "./components/RhDateSelector";
import { dashboardCellKinds, dashboardPivotVariants } from "./builder/dashboardEnums";

const DEFAULT_DATE = { type: "relative", days: 30, frame: "1D" };

// A cell only paints when it carries a component or a report; an empty one left behind in the builder draws
// nothing here.
const columnRenders = (column) => {
    const chart = column?.chart;
    if (!chart) return false;
    return chart.kind === dashboardCellKinds.COMPONENT || !!chart.reportId || !!chart.report;
};

// Whether a section paints anything at all. An empty section renders as a zero-height box, so leaving it in
// the list would contribute nothing but the wrapper's margins - the gap under the header.
const sectionRenders = (section) => {
    let rows = section?.children || [];
    if (section?.divided) rows = rows.flatMap((divider) => divider?.children || []);
    return rows.some((row) => (row?.children || []).some(columnRenders));
};

const DashboardViewColumn = ({ column, dateConfig, isTopRow, reportData }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const chart = column.chart;
    const isPivot = chart?.kind === dashboardCellKinds.PIVOT;
    // Full pivots render live; bounded (top/bottom/movers) render the reduced list from pre-agg.
    const isFullPivot = isPivot && (!chart?.variant || chart?.variant === dashboardPivotVariants.FULL);
    // COMPONENT cells hold a pre-built warehouse widget (no report); they render via the element catalogue.
    const isComponent = chart?.kind === dashboardCellKinds.COMPONENT;
    // This cell's payload (chart series+comparison, or bounded pivot rows). A cell pointing at a saved report
    // is keyed by reportId; a cell carrying its own report has no id to be keyed by, so it uses its own.
    const cellData = reportData?.[chart?.reportId ?? column.id];

    return (
        <Box
            sx={{
                flex: column.width ?? 1,
            }}
        >
            {isComponent && <RhDashboardComponent elementKey={chart.componentKey} sx={chart?.sx} />}
            {(chart?.reportId || chart?.report) && (
                <>
                    {!isPivot && <RhDashboardChart chart={chart} data={cellData} isTopRow={isTopRow} />}
                    {isPivot && !isFullPivot && <RhDashboardPivotBounded chart={chart} data={cellData} isTopRow={isTopRow} />}
                    {isFullPivot && <RhDashboardPivotFull chart={chart} />}
                </>
            )}
        </Box>
    );
};

const DashboardViewRow = ({ row, dateConfig, isTopRow, reportData }) => (
    <Box sx={{ display: "flex", gap: 2,flex:1, }}>
        {(row.children || []).map((column) => (
            <DashboardViewColumn key={column.id} column={column} dateConfig={dateConfig} isTopRow={isTopRow} reportData={reportData} />
        ))}
    </Box>
);

const DashboardViewSection = ({ section, dateConfig, isTopRow, reportData }) => {
    if (section.divided) {
        return (
            <Box sx={{ display: "flex", gap: 2 }}>
                {section.children.map((divider, di) => (
                    <Box 
                        className="sh-div-section"
                        key={di} 
                        sx={{ 
                            flex: divider.width ?? 1,
                            display: "flex", 
                            flexDirection: "column", 
                            gap: 2
                        }}
                    >
                        {(divider.children || []).map((row, ri) => (
                            <DashboardViewRow key={ri} row={row} dateConfig={dateConfig} isTopRow={isTopRow} reportData={reportData} />
                        ))}
                    </Box>
                ))}
            </Box>
        );
    }
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {(section.children || []).map((row, ri) => (
                <DashboardViewRow key={ri} row={row} dateConfig={dateConfig} isTopRow={isTopRow} reportData={reportData} />
            ))}
        </Box>
    );
};

// `id` falls back to the route param, so this renders standalone at /dashboard/:id AND embedded (e.g. the
// warehouse home page passes the user's primary dashboard id directly).
const DashboardViewer = ({ id: idProp,hideControls, config: inlineConfig }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { id: idParam } = useParams();
    const id = idProp ?? idParam;
    const navigate = useNavigate();
    // Both environments run the same hub, only under their own route prefix.
    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const envPrefix = selectedEnvironmentType === ENV_TYPES[200] ? "/relation" : "/warehouse";

    // A dashboard is either looked up by id, or handed in whole by a page - one that carries the reports its
    // cells draw rather than pointing at saved ones, and is posted as-is to have them computed.
    const { data } = useGetBiDashboardByIdQuery(id, { skip: !id || !!inlineConfig });
    const { data: inlineData } = useGetBiInlineDashboardQuery(inlineConfig, { skip: !inlineConfig });
    const [deleteDashboard, { isLoading: deletingDashboard }] = useDeleteBiDashboardMutation();
    const saved = data?.data;
    const config = inlineConfig ?? saved?.config;
    const layout = config?.dashboard;
    // Data for every cell, returned in the same call as the layout. Keyed by reportId.
    const reportData = data?.reportData || inlineData?.reportData || {};

    const [activeTab, setActiveTab] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    // The single dashboard-wide date, held in a report-shaped object so RhDateSelector can drive it.
    const [dateReport, setDateReport] = useState({ date: DEFAULT_DATE });

    useEffect(() => {
        if (!layout) return;
        const tabBody = layout.tabBody;
        const tabKeys = Object.keys(tabBody?.tabs || {});
        setActiveTab(tabBody && tabBody.tabs?.[tabBody.activeTabKey] ? tabBody.activeTabKey : tabKeys[0] || null);
        if (config.date) setDateReport({ date: config.date });
    }, [layout, config]);

    // Same delete as the builder's, so a saved dashboard can be dropped without opening the edit page first.
    const handleDeleteDashboard = () => {
        if (!id || deletingDashboard) return;
        deleteDashboard(id).unwrap()
            .then((res) => { showToastMessagePostRequest(t, res); navigate(`${envPrefix}/dashboard`); })
            .catch((err) => { console.error(err); showToastMessageRequestError(t("delete_failed"), t, err); });
    };

    const dateConfig = dateReport.date;
    const tabBody = layout?.tabBody;
    const tabEntries = Object.entries(tabBody?.tabs || {});
    const sections = (layout?.sections || []).filter(sectionRenders);

    // The dashboard's freshness = the most recent lastCalculatedAt across its cells' pre-agg data.
    const lastCalculatedAt = useMemo(() => {
        const times = Object.values(reportData).map((d) => d?.lastCalculatedAt).filter(Boolean);
        if (!times.length) return null;
        return times.reduce((a, b) => (new Date(a) > new Date(b) ? a : b));
    }, [reportData]);

    return (
        // Flex column so the tab body can claim whatever height is left below the header and tab strip.
        <Box sx={{ background: colors.bg["tertiary"], width: 1, minHeight: "100vh", pt: 10, display: "flex", flexDirection: "column" }}>
            {/* Header */}
            <Box sx={{ px: 4, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Typography variant="h1" sx={{ color: colors.txt["primary"], fontSize: 24, fontWeight: 1000 }}>
                    {saved?.name ?? config?.name}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {/* Date control moved onto the full pivot. Restore here for a per-dashboard date later. */}
                    {/* <RhDateSelector currentReport={dateReport} setCurrentReport={setDateReport} /> */}
                    {lastCalculatedAt &&
                        <Box
                            sx={{
                                height: 40,
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                px: 2.5,
                                borderRadius: 5,
                                background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                boxShadow: "0px 1px 2px 0px rgba(49, 65, 95, 0.25)",
                            }}
                        >
                            <Text light>{t("last_update")}:</Text>
                            <Subheading>{new Date(lastCalculatedAt).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" })}</Subheading>
                        </Box>
                    }
                    {/* Nothing to edit or delete for an inline dashboard - it lives in the page, not in the builder. */}
                    {!hideControls && !inlineConfig &&
                        <Shbutton color="red" variant="contained" startIcon={<DeleteOutlinedIcon />} onClick={() => setDeleteOpen(true)}>
                            {t("delete_dashboard")}
                        </Shbutton>
                    }
                    {!hideControls && !inlineConfig &&
                        <Shbutton color="primary" variant="contained" startIcon={<EditOutlinedIcon />} onClick={() => navigate(`${envPrefix}/stockhubbi/dashboard/${id}/edit`)}>
                            {t("edit")}
                        </Shbutton>
                    }
                </Box>
            </Box>

            {/* Page-level sections */}
            {sections.length > 0 && (
                <Box sx={{ mt: 4, px: 4, display: "flex", flexDirection: "column", gap: 4, pb: tabBody ? 0 : 4, }}>
                    {sections.map((section, si) => (
                        <DashboardViewSection key={section.id ?? si} section={section} dateConfig={dateConfig} isTopRow={si === 0} reportData={reportData} />
                    ))}
                </Box>
            )}

            {/* Tab strip — only shown when the tab body has more than one tab */}
            {tabEntries.length > 1 && (
            <Box sx={{ mt: 4, px: 4, display: "flex", gap: 1 }}>
                {tabEntries.map(([tabKey, tab]) => (
                    <ButtonBase
                        key={tabKey}
                        className={cx(undefined, { active: tabKey === activeTab })}
                        onClick={() => setActiveTab(tabKey)}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            height: 40,
                            fontSize: 14,
                            fontFamily: "Roboto,sans-serif",
                            color: colors.txt["secondary"],
                            fontWeight: 500,
                            "&:after": {
                                content: '""',
                                position: "absolute",
                                width: 1,
                                bottom: 0,
                                borderRadius: 1,
                                height: "2px",
                            },
                            "&.active": {
                                color: colors.txt["primary"],
                                fontWeight: 700,
                                "&:after": { background: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400] },
                            },
                            "&:hover": { color: colors.txt["primary"], backgroundColor: "transparent" },
                        }}
                    >
                        {tab.name}
                    </ButtonBase>
                ))}
            </Box>
            )}

            {/* Active tab content — only shown when a tab body exists */}
            {tabBody && (
            <Box
                sx={{
                    mt: tabEntries.length > 1 ? 0 : 4,
                    width: 1,
                    // Runs to the bottom of the viewport even when the tab holds little content.
                    flex: 1,
                    position: "relative",
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    pt: 4,
                    px: 4,
                    pb: 8,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    "&:before": {
                        content: "''",
                        display: "block",
                        position: "absolute",
                        left: 0,
                        zIndex: 0,
                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        top: "0",
                        height: "48px",
                        width: "24px",
                        borderTopLeftRadius: "16px",
                        boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                        transition: "250ms",
                    },
                    "&:after": {
                        content: "''",
                        display: "block",
                        position: "absolute",
                        top: "0",
                        right: 0,
                        zIndex: 0,
                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        height: "48px",
                        width: "24px",
                        borderTopLeftRadius: "16px",
                        boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                        transform: "scaleX(-1)",
                        transition: "250ms",
                    },
                }}
            >
                {activeTab && (tabBody?.tabs?.[activeTab]?.children || []).map((section, si) => (
                    <DashboardViewSection key={section.id ?? si} section={section} dateConfig={dateConfig} isTopRow={si === 0} reportData={reportData} />
                ))}
            </Box>
            )}

            <MConfirmDelete
                open={deleteOpen}
                handleClose={() => setDeleteOpen(false)}
                confirm={handleDeleteDashboard}
                data={saved?.name}
            />
        </Box>
    );
};

export default DashboardViewer;
